import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import { useEffect, useState } from "react";
import { XMarkIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import Select from "react-select";
import { Input } from "@material-tailwind/react";
import { addVendorPrice, fetchRFQProduct, getPriceVrientVendor, getRfqById, getRfqSummary, getVendorPrice, resetStatus, saveRfqProductMargin } from "@/store/slices/rfqSlice";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Toaster from "@/components/Toaster";
import ShowLoader from "@/components/Loader";
import ScrollToTop from "@/components/ScrollToTop";
import FilePreview from "@/components/FilePreview";
import { fetchCountry } from "@/store/slices/internalUsersSlice";
import { fetchCity } from "@/store/slices/productSlice";
import DeliveryLocationSearch from "@/components/DeliveryLocationSearch";
import { useTranslation } from "react-i18next";
import useHasPermission from "@/components/useHasPermission";

const schema = (maxQty, sale_price) =>
    yup.object().shape({
        vendors: yup
            .array()
            .of(
                yup.object().shape({
                    vendor_id: yup.number().required("vendor_required"),
                    country_id: yup.number().required("country_required"),
                    city_id: yup.number().required("city_required"),

                    pickup_location: yup
                        .string()
                        .trim()
                        .required("pickup_location_required")
                        .min(3, "pickup_location_min")
                        .max(150, "pickup_location_max"),
                    pickup_landmark: yup
                        .string()
                        .trim()
                        .required("landmark_required"),
                    latitude: yup
                        .mixed()
                        .notRequired(),
                    longitude: yup
                        .mixed()
                        .notRequired(),

                    price: yup.number().when("vendor_type", {
                        is: "Avon Vendor",
                        then: (s) =>
                            s
                                .typeError("must_be_a_number")
                                .min(0, "cannot_be_negative")
                                .required("price_required_for_avon"),
                        otherwise: (s) => s.notRequired(),
                    }),

                    vendor_file: yup
                        .mixed()
                        .nullable()
                        .when("vendor_type", {
                            is: (val) => val === "Avon Vendor",
                            then: (schema) =>
                                schema
                                    .required("file_required_for_avon")
                                    .test(
                                        "fileType",
                                        "file_type_image_pdf_only",
                                        (file) => {
                                            if (!file) return false;
                                            if (!(file instanceof File)) return true;

                                            return (
                                                file.type.startsWith("image/") ||
                                                file.type === "application/pdf"
                                            );
                                        }
                                    ),
                            otherwise: (schema) => schema.notRequired(),
                        }),

                    quantity: yup
                        .number()
                        .transform((value, originalValue) =>
                            originalValue === "" ? undefined : value
                        )
                        .typeError("must_be_a_number")
                        .positive("quantity_positive")
                        .required("quantity_required"),
                })
            )
            .test("total-qty-exact", function (vendors = []) {
                const total = vendors.reduce(
                    (sum, v) => sum + Number(v.quantity || 0),
                    0
                );

                if (total > maxQty) {
                    return this.createError({
                        message: `total_quantity_exceed_error#{"maxQty":"${maxQty}"}`,
                    });
                }

                if (total < maxQty) {
                    return this.createError({
                        message: `total_quantity_exact_error#{"maxQty":"${maxQty}"}`,
                    });
                }

                return true;
            })
            .test("total-price-range", function (vendors = []) {
                if (!vendors || vendors.length === 0) return true;

                const totalVendorCost = vendors.reduce(
                    (sum, v) => sum + (Number(v.price || 0) * Number(v.quantity || 0)),
                    0
                );

                const maxTotalThreshold = Number(sale_price || 0) * Number(maxQty || 0);

                if (maxTotalThreshold > 0 && totalVendorCost > maxTotalThreshold) {
                    return this.createError({
                        message: `total_cost_exceed_error#{"totalVendorCost":"${totalVendorCost.toFixed(2)}","maxTotalThreshold":"${maxTotalThreshold.toFixed(2)}"}`,
                    });
                }

                return true;
            }),
    });


export function VendorVarientPriceModal({ onClose, item, rfq_id }) {
    const dispatch = useDispatch();
    const [isError, setIsError] = useState("");
    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const hasViewProductPurchasePricePermission = useHasPermission(`product-purchase-price.view`);
    const hasViewProductSalePricePermission = useHasPermission(`product-sale-price.view`);

    const { loading } = useSelector((state) => state.rfq);

    const initialValue = {
        rfq_product_id: item?.id,
        product_id: item?.product_id,
        vendors: [
            {
                vendor_id: null,
                quantity: 1,
                price: null,
                pickup_location: "",
                pickup_landmark: "",
                latitude: null,
                longitude: null,
            },
        ],
    };

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
        reset
    } = useForm({
        resolver: yupResolver(schema(item?.quantity, item?.sale_price)),
        defaultValues: initialValue,
    });

    const vendorRows = watch("vendors");

    const totalQty = vendorRows?.reduce(
        (sum, row) => sum + (Number(row.quantity) || 0),
        0
    );

    useEffect(() => {
        dispatch(
            getPriceVrientVendor({
                color_id: item.attribute_value_color_id,
                product_id: item.product_id,
                size_id: item.attribute_value_size_id,
                index: 0
            })
        );
        dispatch(
            getVendorPrice({
                rfq_product_id: item.id,
            })
        );
        dispatch(fetchCountry());
    }, []);

    const { vendors, rfq_product_vendors_prices } = useSelector((state) => state.rfq);
    const { countries } = useSelector((s) => s.internalUser);
    const { cities } = useSelector((s) => s.product);

    useEffect(() => {
        if (rfq_product_vendors_prices?.length > 0) {

            const formatted = rfq_product_vendors_prices.map((row) => ({
                vendor_id: row.vendor_id,
                quantity: row.quantity,
                price: row.price,
                previous_price: row.previous_price,
                vendor_file: row.vendor_file,
                country_id: row.country_id,
                city_id: row.city_id,
                pickup_location:
                    row.pickup_location ?? "",
                pickup_landmark:
                    row.pickup_landmark ?? "",
                latitude: row.latitude ?? row.lat ?? null,
                longitude: row.longitude ?? row.lng ?? null,
            }));

            setValue("vendors", formatted);
            rfq_product_vendors_prices?.map((row, index) => {
                dispatch(fetchCity({ country_id: row.country_id, index: index }));
                dispatch(
                    getPriceVrientVendor({
                        color_id: item.attribute_value_color_id,
                        product_id: item.product_id,
                        size_id: item.attribute_value_size_id,
                        country_id: row.country_id,
                        city_id: row.city_id,
                        index: index
                    })
                );

            });
        } else {
            reset(initialValue);
        }
    }, [rfq_product_vendors_prices]);

    useEffect(() => {
        if (countries?.length > 0 && rfq_product_vendors_prices?.length === 0) {
            const saudiArabia = countries.find(c => c.label === 'Saudi Arabia');
            if (saudiArabia) {
                setValue('vendors.0.country_id', saudiArabia.value, { shouldValidate: true });
                dispatch(fetchCity({ country_id: saudiArabia.value, index: 0 }));
            }
        }
    }, [countries, rfq_product_vendors_prices, setValue, dispatch]);


    const addRow = () => {
        const updated = [
            ...vendorRows,
            {
                vendor_id: null,
                quantity: "",
                price: null,
                pickup_location: "",
                pickup_landmark: "",
                latitude: null,
                longitude: null,
            },
        ];
        setValue("vendors", updated);
    };

    const removeRow = (index) => {
        const updated = vendorRows.filter((_, i) => i !== index);
        setValue("vendors", updated);
    };

    const onSubmit = async (data) => {
        try {
            const result = await dispatch(addVendorPrice(data)).unwrap();
            if (result.status) {
                Toaster.success(ct('vendorPriceSaveSuccess'));
                dispatch(resetStatus());
                reset(initialValue);
                close();
                dispatch(getRfqById(rfq_id));
            } else {
                setIsError(result.message || ct("somethingWentWrong"));
                setTimeout(() => {
                    setIsError('');
                }, 8000);
                dispatch(resetStatus());
            }
        } catch (err) {
            setIsError(err.message || ct("somethingWentWrong"));
            setTimeout(() => {
                setIsError('');
            }, 8000);
            dispatch(resetStatus());
        }
    };

    // const handlePriceChange = () => {
    //     dispatch(saveRfqProductMargin({ rfq_product_id: item.id, product_id: item.product_id })).unwrap().then((res) => {
    //         if (res?.status) {
    //             dispatch(fetchRFQProduct({ rfq_id: rfq_id }));
    //             dispatch(getRfqSummary(rfq_id));
    //         } else {
    //             Toaster.error(res.message);
    //         }
    //     }).catch((error) => {
    //         Toaster.error("Changes failed, please try again.");
    //     });
    // }

    const variant = item?.product?.varients?.find(
        (row) =>
            Number(row?.attribute_value_color_id) === Number(item?.attribute_value_color_id) &&
            Number(row?.attribute_value_size_id) === Number(item?.attribute_value_size_id)
    );

    const close = () => {
        reset(initialValue);
        onClose();
    }

    const handleBoxClick = (index) => {
        document.getElementById(`po-pdf-vendor-${index}`)?.click();
    };

    const handleFileChange = (e, index) => {
        const file = e.target.files[0];
        setValue(`vendors.${index}.vendor_file`, file, { shouldValidate: true });
    };

    const vendor_data = watch('vendors');

    const selectedColorVariant = item?.product?.product_varients?.find(
        (variant) => variant.color_id === item.attribute_value_color_id
    );

    const selectedSizeVariant = selectedColorVariant?.sizes?.find(
        (size) => size.size_id === item.attribute_value_size_id
    );

    const colorName = selectedColorVariant?.color;
    const colorHex = selectedColorVariant?.color_hex;
    const sizeName = selectedSizeVariant?.size;
    return (
        <Dialog open={true} handler={close} size="lg" className="rounded-2xl p-0">
            {loading && <ShowLoader />}
            <ScrollToTop />
            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-2xl font-bold text-gray-900">
                    {t('choose_vendor_variant_price_title')}
                </h2>
                {/* <button
                    onClick={close}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button> */}
            </DialogHeader>

            <DialogBody className="px-6 max-h-[65vh] overflow-y-auto">

                {/* Product block */}
                <div className="flex items-center space-x-4 mb-6">
                    <img
                        src={item?.product_image || '/img/photo-gallery.png'}
                        className="w-40 h-40 object-cover rounded-md border"
                        alt="product"
                    />
                    <div>
                        <p className="font-semibold text-gray-900 text-lg leading-tight">
                            {t('product_name_label')} :  {item?.product?.name}
                        </p>
                        <p className="text-sm font-medium text-gray-600">
                            {t('total_quantity_label')}: <span className="text-gray-900">{item?.quantity}</span>
                        </p>

                        <div className="flex flex-wrap gap-2 mt-2">
                            {/* Color & Size Badges */}
                            {colorName && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded flex items-center gap-2 border">
                                {ct('color')}: {colorName}
                                {colorHex && (
                                    <span
                                        className="w-3 h-3 rounded-full border border-gray-300"
                                        style={{ backgroundColor: colorHex }}
                                    ></span>
                                )}
                            </span>}

                            {sizeName && <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded border">
                                {ct('size')}: {sizeName}
                            </span>}
                        </div>

                        {/* Purchase and Sale Price Info */}
                        {(hasViewProductPurchasePricePermission || hasViewProductSalePricePermission) && (
                            <div className="flex gap-4 mt-2 border-t pt-2 border-dashed border-gray-300">
                                {hasViewProductPurchasePricePermission && (
                                    <div>
                                        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{t('purchase_price_label')}</p>
                                        <p className="text-sm font-semibold text-blue-700">{item?.product?.rfq_currency_code} {item?.unit_price}</p>
                                    </div>
                                )}
                                {hasViewProductSalePricePermission && (
                                    <>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{t('sale_price_label')}</p>
                                            <p className="text-sm font-semibold text-green-700">{item?.product?.rfq_currency_code} {item?.sale_price}</p>
                                        </div>
                                        <div className="ml-auto text-right">
                                            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{t('total_budget_label')}</p>
                                            <p className="text-sm font-bold text-gray-900">
                                                {item?.product?.rfq_currency_code}  {(Number(item?.sale_price || 0) * Number(item?.quantity || 0)).toFixed(2)}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {isError &&
                    <div class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                        <span class="font-medium">{ct('oops')}</span> {isError}
                    </div>
                }
                {errors?.vendors?.root?.message && (
                    <div class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
                        <span class="font-medium">{ct('oops')}</span> {t(errors.vendors?.root.message.split('#')[0], { maxQty: JSON.parse(errors.vendors?.root.message.split('#')[1] || '{}')?.maxQty, totalVendorCost: JSON.parse(errors.vendors?.root.message.split('#')[1] || '{}')?.totalVendorCost, maxTotalThreshold: JSON.parse(errors.vendors?.root.message.split('#')[1] || '{}')?.maxTotalThreshold })}
                    </div>
                )}

                <div className="space-y-6 mt-4">
                    {vendorRows.map((row, index) => {
                        const selectedVendor = vendors?.find(
                            (v) => v.value === row.vendor_id
                        );

                        const isEditable =
                            selectedVendor?.label === "Avon Vendor" ||
                            !selectedVendor?.price ||
                            Number(selectedVendor?.price) === 0;

                        const uploadedFile = watch(`vendors.${index}.vendor_file`);
                        const vendorType = watch(`vendors.${index}.vendor_type`);

                        const fileToPreview = uploadedFile || row?.vendor_file || null;
                        const showUploadBox = vendorType === 'Avon Vendor' || !!uploadedFile;

                        const otherVendorsQty = vendorRows
                            .filter((_, i) => i !== index)
                            .reduce(
                                (sum, r) => sum + (Number(r.quantity) || 0),
                                0
                            );
                        const maxAllowedQty = item?.quantity - otherVendorsQty;

                        return (
                            <div
                                key={index}
                                className="border p-5 rounded-xl bg-white shadow-sm relative"
                            >
                                {/* Row Header */}
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="font-semibold text-gray-800">
                                        {t('vendor_number_label', { index: index + 1 })}
                                    </h3>

                                    {vendorRows.length > 1 && (
                                        <button
                                            onClick={() => removeRow(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>

                                {/* Country */}
                                <div className="mb-4">
                                    <Typography variant="small" className="font-medium capitalize">
                                        {t('country_label')}
                                    </Typography>
                                    <Select
                                        placeholder={t('select_country_placeholder')}
                                        onChange={(country) => {
                                            setValue(
                                                `vendors.${index}.country_id`,
                                                country?.value || null,
                                                { shouldValidate: true }
                                            );
                                            dispatch(fetchCity({ country_id: country?.value, index: index }));
                                            dispatch(
                                                getPriceVrientVendor({
                                                    color_id: item.attribute_value_color_id,
                                                    product_id: item.product_id,
                                                    size_id: item.attribute_value_size_id,
                                                    country_id: country?.value,
                                                    city_id: watch(`vendors.${index}.city_id`),
                                                    index: index
                                                })
                                            );
                                        }}
                                        options={countries}
                                        value={
                                            countries?.find(
                                                (opt) =>
                                                    opt.value ===
                                                    watch(`vendors.${index}.country_id`)
                                            ) || null
                                        }
                                    // isClearable
                                    />
                                    {errors?.vendors?.[index]?.country_id && (
                                        <p className="text-red-600 text-sm mt-1">
                                            {t(errors.vendors[index].country_id.message)}
                                        </p>
                                    )}
                                </div>
                                {/* City */}
                                <div className="mb-4">
                                    <Typography variant="small" className="font-medium capitalize">
                                        {t('city_label')}
                                    </Typography>
                                    <Select
                                        placeholder={t('select_city_placeholder')}
                                        onChange={(city) => {
                                            setValue(
                                                `vendors.${index}.city_id`,
                                                city?.value || null,
                                                { shouldValidate: true }
                                            );
                                            dispatch(
                                                getPriceVrientVendor({
                                                    color_id: item.attribute_value_color_id,
                                                    product_id: item.product_id,
                                                    size_id: item.attribute_value_size_id,
                                                    country_id: watch(`vendors.${index}.country_id`),
                                                    city_id: city?.value,
                                                    index: index
                                                })
                                            );
                                        }}
                                        options={cities?.length > 0 ? cities[index] : []}
                                        value={
                                            cities?.length > 0 ? cities[index]?.find(
                                                (opt) =>
                                                    opt.value ===
                                                    watch(`vendors.${index}.city_id`)
                                            ) || null : null
                                        }
                                    // isClearable
                                    />
                                    {errors?.vendors?.[index]?.city_id && (
                                        <p className="text-red-600 text-sm mt-1">
                                            {t(errors.vendors[index].city_id.message)}
                                        </p>
                                    )}
                                </div>

                                {/* Vendor Dropdown */}
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('select_vendor_placeholder')}
                                </label>

                                <Controller
                                    name={`vendors.${index}.vendor_id`}
                                    control={control}
                                    render={({ field }) => {
                                        const selectedVendorIds = vendorRows
                                            ?.map((v, i) => (i !== index ? v.vendor_id : null))
                                            .filter(Boolean);

                                        const filteredVendors = vendors?.length > 0 ? vendors[index]?.filter(
                                            (v) => !selectedVendorIds.includes(v.value)
                                        ) : [];

                                        return (
                                            <Select
                                                placeholder={t('select_vendor_placeholder')}
                                                options={filteredVendors || []}
                                                value={vendors?.length > 0 ? vendors[index]?.find((v) => v.value === field.value) || null : null}
                                                onChange={(vendor) => {
                                                    field.onChange(vendor?.value);

                                                    setValue(`vendors.${index}.vendor_type`, vendor?.vendor_type || "");

                                                    if (vendor?.price && Number(vendor.price) > 0) {
                                                        setValue(`vendors.${index}.price`, vendor.price);
                                                    } else {
                                                        setValue(`vendors.${index}.price`, null);
                                                    }
                                                }}
                                                className="mb-1"
                                                // isClearable
                                                noOptionsMessage={() => t('no_vendor_available')}
                                            />
                                        );
                                    }}
                                />
                                {errors?.vendors?.[index]?.vendor_id && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {t(errors.vendors[index].vendor_id.message)}
                                    </p>
                                )}


                                {/* Pickup Location  */}
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                                        {t('pickup_location_label')}
                                    </label>
                                    {/* <Input
                                        type="text"
                                        value={row?.pickup_location || ""}
                                        className="!border-t-blue-gray-200"
                                        labelProps={{
                                            className: "before:content-none after:content-none",
                                        }}
                                        placeholder="Enter full pickup address (street, area, city)"
                                        onChange={(e) => {
                                            setValue(
                                                `vendors.${index}.pickup_location`,
                                                e.target.value,
                                                { shouldValidate: true }
                                            );
                                        }}
                                    />
                                    {errors?.vendors?.[index]?.pickup_location && (
                                        <p className="text-red-600 text-sm mt-1">
                                            {errors.vendors[index].pickup_location.message}
                                        </p>
                                    )} */}
                                    <DeliveryLocationSearch
                                        value={row?.pickup_location || ""}
                                        onChange={({
                                            pickup_location,
                                        }) => {
                                            setValue(
                                                `vendors.${index}.pickup_location`,
                                                String(pickup_location || "").trim(),
                                                { shouldValidate: true }
                                            );
                                        }}
                                        placeholder={t('searchAddressPlaceholder')}
                                        error={errors?.vendors?.[index]?.pickup_location?.message ? t(errors?.vendors?.[index]?.pickup_location?.message) : ''}
                                    />
                                </div>

                                {/* Pickup Landmark */}
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                                        {t('pickup_landmark_label')}
                                    </label>
                                    <DeliveryLocationSearch
                                        value={row?.pickup_landmark || ""}
                                        latitude={row?.latitude}
                                        longitude={row?.longitude}
                                        onChange={({
                                            pickup_landmark,
                                            latitude,
                                            longitude,
                                        }) => {
                                            setValue(
                                                `vendors.${index}.pickup_landmark`,
                                                String(pickup_landmark || "").trim(),
                                                { shouldValidate: true }
                                            );
                                            setValue(
                                                `vendors.${index}.latitude`,
                                                latitude != null
                                                    ? Number(latitude)
                                                    : null,
                                                { shouldValidate: true }
                                            );
                                            setValue(
                                                `vendors.${index}.longitude`,
                                                longitude != null
                                                    ? Number(longitude)
                                                    : null,
                                                { shouldValidate: true }
                                            );
                                        }}
                                        placeholder={t('searchLandmarkPlaceholder')}
                                        error={
                                            errors?.vendors?.[index]?.pickup_landmark?.message
                                                ? t(errors?.vendors?.[index]?.pickup_landmark?.message)
                                                : (errors?.vendors?.[index]?.latitude?.message || errors?.vendors?.[index]?.longitude?.message)
                                                    ? t('enter_valid_address') : ''
                                        }
                                    />
                                </div>

                                {/* Price */}
                                <label className="block text-sm font-medium text-gray-700 mt-3 capitalize">
                                    {t('price_label')} {isEditable ? '' : `(${ct('autoFilled')})`}
                                </label>
                                {row?.previous_price && Number(row.previous_price) > 0 && (
                                    <div className="text-xs text-gray-500 font-medium flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                        <span>{t('previous_price_label')}:</span>
                                        <span className="text-red-600 font-semibold">
                                            {item?.product?.rfq_currency_code || ""} {Number(row.previous_price).toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex items-stretch">
                                    <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-blue-gray-200 rounded-l-md text-sm font-medium text-gray-700">
                                        {item?.product?.rfq_currency_code || ""}
                                    </div>

                                    <Controller
                                        name={`vendors.${index}.price`}
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                min="0"
                                                type="number"
                                                value={field.value || ""}
                                                readOnly={!isEditable}
                                                className={`!border-t-blue-gray-200 ${!isEditable ? "bg-gray-100 cursor-not-allowed" : ""
                                                    }`}
                                                labelProps={{
                                                    className: "before:content-none after:content-none",
                                                }}
                                                placeholder={t('vendor_price_placeholder')}
                                                onChange={(e) => {
                                                    if (isEditable) {
                                                        setValue(`vendors.${index}.price`, e.target.value);
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                                {errors?.vendors?.[index]?.price && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {t(errors.vendors[index].price.message)}
                                    </p>
                                )}

                                {/* Quantity */}
                                <label className="block text-sm font-medium text-gray-700 mt-3 capitalize">
                                    {t('quantity_label')} ({ct('max')} {maxAllowedQty})
                                </label>

                                <Controller
                                    name={`vendors.${index}.quantity`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="number"
                                            min="1"
                                            max={maxAllowedQty}
                                            {...field}
                                            className={`!border-t-blue-gray-200`}
                                            labelProps={{
                                                className: "before:content-none after:content-none",
                                            }}
                                            placeholder={t('enter_product_quantity_placeholder')}
                                        />
                                    )}
                                />
                                {errors?.vendors?.[index]?.quantity && (
                                    <p className="text-red-600 text-sm mt-1">
                                        {t(errors.vendors[index].quantity.message)}
                                    </p>
                                )}
                                {showUploadBox && <div>
                                    <label className="block text-sm font-medium mb-2 mt-3 capitalize">
                                        {t('upload_file_label')} <span className="text-red-600">*</span>
                                    </label>

                                    <div
                                        onClick={() => handleBoxClick(index)}
                                        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-500 transition mb-5"
                                    >
                                        <p className="text-gray-700 font-medium">{t('click_to_upload_file')}</p>
                                        <p className="text-xs text-gray-500 mt-1">{t('file_type_image_pdf_only')}</p>

                                        <input
                                            type="file"
                                            id={`po-pdf-vendor-${index}`}
                                            accept="image/*,application/pdf"
                                            onChange={(e) => handleFileChange(e, index)}
                                            className="hidden"
                                        />

                                    </div>
                                    {fileToPreview && (
                                        <FilePreview
                                            file={fileToPreview}
                                            onRemove={() => setValue(`vendors.${index}.vendor_file`, null)}
                                            isView={false}
                                        />
                                    )}

                                    {errors?.vendors?.[index]?.vendor_file && (
                                        <p className="text-red-600 text-sm mt-1">
                                            {t(errors.vendors[index].vendor_file.message)}
                                        </p>
                                    )}

                                </div>}
                            </div>
                        );
                    })}
                </div>
                <div className="flex justify-center mt-6">
                    <button
                        type="button"
                        disabled={
                            totalQty >= item?.quantity ||
                            !vendorRows[vendorRows.length - 1]?.quantity
                        }
                        onClick={() => {
                            if (totalQty < item?.quantity) {
                                addRow();
                            }
                        }}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-colors
                            ${totalQty < item?.quantity && vendorRows[vendorRows.length - 1]?.quantity ? "bg-yellow-400 hover:bg-yellow-500" : "bg-gray-300 cursor-not-allowed"}
                        `}
                    >
                        <PlusIcon className="w-5 h-5" />
                        {t('add_another_vendor_button')}
                    </button>
                </div>

            </DialogBody>

            <DialogFooter className="flex justify-end gap-4 border-t pt-4">
                <button
                    type="button"
                    onClick={close}
                    className="px-5 py-2 rounded-lg text-gray-700 hover:bg-gray-100"
                >
                    {ct('cancel')}
                </button>

                <button
                    onClick={handleSubmit(onSubmit)}
                    className="px-5 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-medium shadow-sm"
                >
                    {t('save_vendors_button')}
                </button>
            </DialogFooter>
        </Dialog>
    );
}

export default VendorVarientPriceModal;
